<script setup lang="ts">
import { computed } from 'vue';

import { useFieldAttrs } from '@shared/ui/field-attrs';

defineOptions({
  inheritAttrs: false,
});

interface Props {
  value: string | number | boolean;
  disabled?: boolean;
  name?: string;
  /** Без кружка: выбранный вариант рисует слот через `peer-checked:`. */
  bare?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  name: undefined,
  bare: false,
});

const modelValue = defineModel<string | number | boolean | null>();

const inputAttrs = useFieldAttrs();

const isChecked = computed(() => modelValue.value === props.value);
</script>

<template>
  <label
    :class="[
      bare
        ? 'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-(--accent)'
        : 'radio-wrapper',
      { 'cursor-pointer': bare && !disabled, 'radio-disabled': disabled },
      $attrs.class,
    ]"
    :style="$attrs.style"
  >
    <input
      v-bind="inputAttrs"
      v-model="modelValue"
      type="radio"
      :name="name"
      :value="value"
      :disabled="disabled"
      class="peer sr-only"
    />
    <span
      v-if="!bare"
      class="radio-outer"
      :class="{ 'radio-checked': isChecked }"
      aria-hidden="true"
    >
      <span
        v-if="isChecked"
        class="radio-inner"
      />
    </span>
    <slot />
  </label>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  .radio-wrapper {
    @apply inline-flex cursor-pointer items-center gap-2;
  }

  .radio-disabled {
    @apply cursor-not-allowed opacity-50;
  }

  .radio-outer {
    @apply relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200;
    border-color: var(--field-border);
    background-color: var(--surface-overlay);
  }

  .radio-wrapper:not(.radio-disabled):hover .radio-outer {
    border-color: var(--accent);
  }

  .peer:focus-visible + .radio-outer {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .radio-outer.radio-checked {
    border-color: var(--accent);
    background-color: var(--surface-overlay);
  }

  .radio-inner {
    @apply h-2.5 w-2.5 rounded-full transition-all duration-200;
    background-color: var(--accent);
    animation: radio-scale-in 0.2s ease-out;
  }

  @keyframes radio-scale-in {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
}
</style>
